import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import UidCard from './UidCard';
import useFarmContext from './useFarmContext';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 24;

const sortOptions = [
  { value: 'lastSeen', label: 'Last seen' },
  { value: 'firstSeen', label: 'First seen' },
  { value: 'scansCount', label: 'Scan count' },
];

export default function FarmMain() {
  const { getToken } = useAuth();
  const { tenant, refreshToken } = useFarmContext();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState('lastSeen');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState({ data: [], total: 0, pageSize: PAGE_SIZE });

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sort, order]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Missing auth token');
        }

        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
          sort,
          order,
        });
        if (debouncedSearch) {
          params.set('search', debouncedSearch);
        }

        const responseRaw = await fetch(`${API_BASE}/farm/${tenant}/uids?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!responseRaw.ok) {
          const body = await responseRaw.json().catch(() => ({}));
          throw new Error(body?.error || `Request failed (${responseRaw.status})`);
        }

        const body = await responseRaw.json();
        if (!cancelled) {
          setResponse({
            data: Array.isArray(body.data) ? body.data : [],
            total: Number(body.total) || 0,
            pageSize: Number(body.pageSize) || PAGE_SIZE,
          });
        }
      } catch (err) {
        console.error('Failed to load farm UID list', err);
        if (!cancelled) {
          setError('Unable to load the herd right now. Please try again.');
          setResponse({ data: [], total: 0, pageSize: PAGE_SIZE });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (tenant) {
      load();
    }

    return () => {
      cancelled = true;
    };
  }, [tenant, page, sort, order, debouncedSearch, getToken, refreshToken]);

  const totalPages = Math.max(1, Math.ceil((response.total || 0) / (response.pageSize || PAGE_SIZE)));

  const cards = useMemo(() => {
    return response.data.map(item => (
      <UidCard key={item.uid} tenant={tenant} {...item} />
    ));
  }, [response.data, tenant]);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={controlsRowStyle}>
        <input
          type="search"
          placeholder="Search by UID or name"
          value={search}
          onChange={event => setSearch(event.target.value)}
          style={searchInputStyle}
        />
        <div style={selectGroupStyle}>
          <label style={labelStyle}>
            Sort by
            <select value={sort} onChange={event => setSort(event.target.value)} style={selectStyle}>
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label style={labelStyle}>
            Order
            <select value={order} onChange={event => setOrder(event.target.value)} style={selectStyle}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </label>
        </div>
      </div>

      {loading && <p>Loading herd data…</p>}
      {error && <p style={{ color: colors.danger }}>{error}</p>}

      {!loading && !error && (
        <>
          {cards.length === 0 ? (
            <p>No devices found for these filters.</p>
          ) : (
            <div style={gridStyle}>{cards}</div>
          )}
          <div style={paginationStyle}>
            <button
              type="button"
              style={{ ...paginationButtonStyle, opacity: page === 1 ? 0.4 : 1 }}
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              style={{ ...paginationButtonStyle, opacity: page >= totalPages ? 0.4 : 1 }}
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const controlsRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1rem',
  alignItems: 'center',
};

const searchInputStyle = {
  flex: '1 1 260px',
  minWidth: '220px',
  padding: '0.65rem 1rem',
  borderRadius: '999px',
  border: `1px solid ${colors.border}`,
  fontSize: '1rem',
};

const selectGroupStyle = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: '0.85rem',
  color: colors.muted,
  gap: '0.3rem',
};

const selectStyle = {
  padding: '0.6rem 0.9rem',
  borderRadius: '10px',
  border: `1px solid ${colors.border}`,
  fontSize: '0.95rem',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '1.25rem',
};

const paginationStyle = {
  display: 'flex',
  gap: '1rem',
  alignItems: 'center',
  justifyContent: 'flex-end',
};

const paginationButtonStyle = {
  backgroundColor: colors.primary,
  color: colors.background,
  border: 'none',
  borderRadius: '999px',
  padding: '0.45rem 1.2rem',
  fontWeight: 600,
  cursor: 'pointer',
};
